import {ScreenLayout} from "@/components/layouts/page/ScreenLayout";
import TableLayout from "@/components/layouts/page/TableLayout";

import {useCallback, useEffect, useMemo, useState} from "react";

import {useDeepCompareEffect} from "@hooks/DeepCompareEffect";
import {
  getDoctors,
  setPage,
  setSearch,
  setSize,
  setSort,
  setTotalPages,
} from "@redux/slices/doctorSlices/tableSlice";
import {ControlButton} from "@ui/buttons/ControlButton";
import {TableButton} from "@ui/buttons/TableButton";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

export const DoctorTablePage = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {demo} = useSelector((state) => state.common.candidate);

  const {
    doctors,
    page,
    size,
    sort,
    search,
    totalPages,
    totalDoctors,
    serverStatus,
  } = useSelector((state) => state.doctor.table);

  const [params, setParams] = useState({
    page: 1,
    size: 5,
    sort,
  });

  const goToPage = (pageIndex) => {
    dispatch(setPage(pageIndex + 1));
  };

  const handleSelectSize = (option) => {
    dispatch(setSize(+option));
    dispatch(setPage(1));
    if (totalDoctors != null && +option > 0) {
      dispatch(setTotalPages(Math.ceil(totalDoctors / +option)));
    }
  };

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
  };

  const handleSearch = useCallback(
    (value) => {
      clearTimeout(window.searchTimeout);
      window.searchTimeout = setTimeout(() => {
        dispatch(setSearch(value?.toLowerCase?.() ?? ""));
        setParams((prev) => ({...prev, page: 1, search: value}));
        dispatch(getDoctors({page: 1, size, search: value, sort}));
      }, 500);
    },
    [dispatch, size, sort],
  );

  const controls = [
    {
      id: 0,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("button.add")}
          link
        />
      ),
    },
  ];

  const columns = useMemo(
    () => [
      {
        Header: t("table.profile_name"),
        accessor: "name",
        sortable: true,
        width: 600,
      },
      {
        Header: t("table.status"),
        accessor: "state",
        sortable: true,
        Cell: ({value}) => (value ? t(`table.status_${value}`) : ""),
        width: 350,
      },
      {
        Header: t("table.created"),
        accessor: "created_at",
        sortable: true,
        Cell: ({value}) => (value ? moment(value).format("DD-MM-YYYY") : ""),
        width: 350,
      },
      {
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div className={"flex justify-end gap-[8rem] phone:gap-[40rem]"}>
            <TableButton
              icon={"view"}
              color={"var(--orange)"}
              label={t("table.button.view")}
              action={"view"}
              item={row.original}
            />
          </div>
        ),
        width: 165,
      },
    ],
    [t],
  );

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      ...(page > 0 ? {page} : {page: 1}),
      ...(size > 0 ? {size} : {size: 5}),
      ...(search && {search}),
      ...(Object.keys(sort).length !== 0 && {sort}),
    }));
  }, [page, size, search, sort]);

  useDeepCompareEffect(() => {
    dispatch(getDoctors(params));
  }, [params]);

  useEffect(() => {
    if (totalDoctors != null && size > 0) {
      dispatch(setTotalPages(Math.ceil(totalDoctors / size)));
    }
  }, [totalDoctors, size, dispatch]);

  useEffect(() => {
    if (
      totalPages != null &&
      totalDoctors != null &&
      page > totalPages &&
      totalDoctors > 0
    ) {
      dispatch(setPage(Math.ceil(totalDoctors / size)));
    }
  }, [totalPages, totalDoctors, size, page, dispatch]);

  return (
    <ScreenLayout
      title={t("page.avatars.title")}
      controlsList={!demo ? controls : []}
    >
      <TableLayout
        serverStatus={serverStatus}
        columns={columns}
        data={doctors}
        dataSort={sort}
        selectSort={handleSelectSort}
        dataSize={size}
        selectSize={handleSelectSize}
        searchPlaceholder={t("table.search_avatars")}
        searchValue={search}
        onSearch={handleSearch}
        totalPages={totalPages ?? 1}
        currentPage={page}
        toPage={goToPage}
      />
    </ScreenLayout>
  );
};
